import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Table, TableBody, TableHead, TableRow, TableCell,
  IconButton, Button, TextField, Chip, Switch,
  FormControl, InputLabel, Select, MenuItem,
} from "@material-ui/core";
import { Tabs, Tab, Stack, Typography } from "@mui/material";
import { Edit, DeleteOutline, Add, Event, Search, CheckCircle, Cancel } from "@material-ui/icons";
import { toast } from "react-toastify";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import DoctorModal from "../../components/DoctorModal";
import AppointmentModal from "../../components/AppointmentModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const localizer = momentLocalizer(moment);

const useStyles = makeStyles((theme) => ({
  mainPaper: { flex: 1, padding: theme.spacing(1), overflowY: "auto", ...theme.scrollbarStyles },
  tab: { textTransform: "none" },
  filterRow: { display: "flex", gap: theme.spacing(1), marginBottom: theme.spacing(2), flexWrap: "wrap", alignItems: "center" },
  calendarWrapper: { height: 600, "& .rbc-calendar": { fontFamily: theme.typography.fontFamily } },
}));

const doctorReducer = (state, action) => {
  switch (action.type) {
    case "LOAD": return action.payload;
    case "UPDATE": return state.map(d => d.id === action.payload.id ? action.payload : d).concat(state.find(d => d.id === action.payload.id) ? [] : [action.payload]);
    case "DELETE": return state.filter(d => d.id !== action.payload);
    default: return state;
  }
};

const appointmentReducer = (state, action) => {
  switch (action.type) {
    case "LOAD": return action.payload;
    case "UPDATE": return state.map(a => a.id === action.payload.id ? action.payload : a).concat(state.find(a => a.id === action.payload.id) ? [] : [action.payload]);
    case "DELETE": return state.filter(a => a.id !== action.payload);
    default: return state;
  }
};

const statusColors = { pending: "#f59e0b", confirmed: "#22c55e", completed: "#3b82f6", cancelled: "#ef4444", no_show: "#6b7280" };
const statusLabels = { pending: "Pendente", confirmed: "Confirmado", completed: "Concluído", cancelled: "Cancelado", no_show: "Faltou" };

const DAY_NAMES = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

const Clinic = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const [doctors, dispatchDoctors] = useReducer(doctorReducer, []);
  const [appointments, dispatchAppointments] = useReducer(appointmentReducer, []);

  const [doctorModalOpen, setDoctorModalOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingDoctor, setDeletingDoctor] = useState(null);

  const [filterDoctor, setFilterDoctor] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDate, setFilterDate] = useState("");

  // Load doctors
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/clinic/doctors");
        dispatchDoctors({ type: "LOAD", payload: data });
      } catch (err) { toastError(err); }
    })();
  }, []);

  // Load appointments
  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDoctor) params.doctorId = filterDoctor;
      if (filterStatus) params.status = filterStatus;
      if (filterDate) params.date = filterDate;
      const { data } = await api.get("/clinic/appointments", { params });
      dispatchAppointments({ type: "LOAD", payload: data });
    } catch (err) { toastError(err); }
    setLoading(false);
  }, [filterDoctor, filterStatus, filterDate]);

  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  const handleDeleteDoctor = async (id) => {
    try {
      await api.delete(`/clinic/doctors/${id}`);
      dispatchDoctors({ type: "DELETE", payload: id });
      toast.success("Médico removido");
    } catch (err) { toastError(err); }
    setDeletingDoctor(null);
  };

  const handleConfirmAppt = async (id) => {
    try {
      const { data } = await api.put(`/clinic/appointments/${id}/confirm`);
      dispatchAppointments({ type: "UPDATE", payload: data });
      toast.success("Consulta confirmada");
    } catch (err) { toastError(err); }
  };

  const handleCancelAppt = async (id) => {
    try {
      const { data } = await api.put(`/clinic/appointments/${id}/cancel`);
      dispatchAppointments({ type: "UPDATE", payload: data });
      toast.success("Consulta cancelada");
    } catch (err) { toastError(err); }
  };

  // Calendar events
  const calendarEvents = appointments.filter(a => a.status !== "cancelled").map(a => ({
    id: a.id,
    title: `${a.patientName} - ${a.doctor?.name || ""}`,
    start: new Date(`${a.date}T${a.startTime}:00`),
    end: new Date(`${a.date}T${a.endTime}:00`),
    resource: a,
  }));

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: statusColors[event.resource.status] || "#3b82f6",
      borderRadius: 6, border: "none", color: "#fff", fontSize: 12,
    },
  });

  if (user.profile === "user") return <ForbiddenPage />;

  return (
    <MainContainer>
      <MainHeader>
        <Title>Clínica</Title>
        <MainHeaderButtonsWrapper>
          {tabIndex === 0 && (
            <Button variant="contained" color="primary" onClick={() => { setSelectedDoctor(null); setDoctorModalOpen(true); }}>
              <Add style={{ marginRight: 4 }} /> Médico
            </Button>
          )}
          {(tabIndex === 1 || tabIndex === 2) && (
            <Button variant="contained" color="primary" onClick={() => { setSelectedAppointment(null); setAppointmentModalOpen(true); }}>
              <Add style={{ marginRight: 4 }} /> Agendamento
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} textColor="primary" indicatorColor="primary" sx={{ mb: 1 }}>
        <Tab label="Médicos" className={classes.tab} />
        <Tab label="Agenda" className={classes.tab} />
        <Tab label="Agendamentos" className={classes.tab} />
      </Tabs>

      {/* TAB 0 - Médicos */}
      {tabIndex === 0 && (
        <Paper className={classes.mainPaper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Especialidade</TableCell>
                <TableCell align="center">Duração</TableCell>
                <TableCell align="center">Horários</TableCell>
                <TableCell align="center">Ativo</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {doctors.map(doc => (
                <TableRow key={doc.id}>
                  <TableCell>{doc.name}</TableCell>
                  <TableCell>{doc.specialization}</TableCell>
                  <TableCell align="center">{doc.consultationDuration} min</TableCell>
                  <TableCell align="center" style={{ fontSize: 11 }}>
                    {doc.availabilities && doc.availabilities.length > 0
                      ? doc.availabilities.map(a => `${DAY_NAMES[a.dayOfWeek]?.substring(0,3)} ${a.startTime}-${a.endTime}`).join(", ")
                      : <Chip label="Sem horários" size="small" />}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={doc.active ? "Sim" : "Não"} size="small" style={{ backgroundColor: doc.active ? "#22c55e" : "#ef4444", color: "#fff" }} />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => { setSelectedDoctor(doc); setDoctorModalOpen(true); }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => { setDeletingDoctor(doc); setConfirmOpen(true); }}><DeleteOutline fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={6} />}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* TAB 1 - Agenda (Calendar) */}
      {tabIndex === 1 && (
        <Paper className={classes.mainPaper} variant="outlined">
          <div className={classes.calendarWrapper}>
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              defaultView="week"
              views={["week", "day", "agenda"]}
              step={30}
              timeslots={1}
              min={new Date(2020, 0, 1, 7, 0)}
              max={new Date(2020, 0, 1, 20, 0)}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={(event) => { setSelectedAppointment(event.resource); setAppointmentModalOpen(true); }}
              messages={{ week: "Semana", day: "Dia", agenda: "Agenda", today: "Hoje", previous: "Anterior", next: "Próximo", noEventsInRange: "Nenhum agendamento neste período" }}
              style={{ height: "100%" }}
            />
          </div>
        </Paper>
      )}

      {/* TAB 2 - Agendamentos (Table) */}
      {tabIndex === 2 && (
        <Paper className={classes.mainPaper} variant="outlined">
          <div className={classes.filterRow}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
              <InputLabel>Médico</InputLabel>
              <Select value={filterDoctor} onChange={e => setFilterDoctor(e.target.value)} label="Médico">
                <MenuItem value="">Todos</MenuItem>
                {doctors.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} label="Status">
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(statusLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField type="date" variant="outlined" size="small" label="Data" InputLabelProps={{ shrink: true }} value={filterDate} onChange={e => setFilterDate(e.target.value)} />
          </div>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Paciente</TableCell>
                <TableCell>Médico</TableCell>
                <TableCell align="center">Data</TableCell>
                <TableCell align="center">Horário</TableCell>
                <TableCell>Serviço</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.map(appt => (
                <TableRow key={appt.id}>
                  <TableCell>{appt.patientName}</TableCell>
                  <TableCell>{appt.doctor?.name || "-"}</TableCell>
                  <TableCell align="center">{moment(appt.date).format("DD/MM/YYYY")}</TableCell>
                  <TableCell align="center">{appt.startTime} - {appt.endTime}</TableCell>
                  <TableCell>{appt.service || "-"}</TableCell>
                  <TableCell align="center">
                    <Chip label={statusLabels[appt.status] || appt.status} size="small" style={{ backgroundColor: statusColors[appt.status], color: "#fff" }} />
                  </TableCell>
                  <TableCell align="center">
                    {appt.status === "pending" && (
                      <>
                        <IconButton size="small" title="Confirmar" onClick={() => handleConfirmAppt(appt.id)}><CheckCircle fontSize="small" style={{ color: "#22c55e" }} /></IconButton>
                        <IconButton size="small" title="Cancelar" onClick={() => handleCancelAppt(appt.id)}><Cancel fontSize="small" style={{ color: "#ef4444" }} /></IconButton>
                      </>
                    )}
                    <IconButton size="small" title="Editar" onClick={() => { setSelectedAppointment(appt); setAppointmentModalOpen(true); }}><Edit fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={7} />}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Modals */}
      <DoctorModal
        open={doctorModalOpen}
        onClose={() => { setDoctorModalOpen(false); setSelectedDoctor(null); }}
        doctor={selectedDoctor}
        onSave={(doc) => {
          dispatchDoctors({ type: "UPDATE", payload: doc });
          // Reload to get availabilities
          api.get("/clinic/doctors").then(({ data }) => dispatchDoctors({ type: "LOAD", payload: data }));
        }}
      />
      <AppointmentModal
        open={appointmentModalOpen}
        onClose={() => { setAppointmentModalOpen(false); setSelectedAppointment(null); }}
        appointment={selectedAppointment}
        doctors={doctors}
        onSave={() => loadAppointments()}
      />
      <ConfirmationModal
        title={`Remover ${deletingDoctor?.name}?`}
        open={confirmOpen}
        onClose={() => { setConfirmOpen(false); setDeletingDoctor(null); }}
        onConfirm={() => deletingDoctor && handleDeleteDoctor(deletingDoctor.id)}
      >
        Todos os agendamentos deste médico serão removidos.
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Clinic;
